/**
 * Driver Service
 * Handles driver-related API calls and utilities
 */

import { httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, functions } from '../config/firebase';

/**
 * Request all online drivers to send their current location via FCM
 * Calls the requestDriverLocations Cloud Function
 * 
 * @returns {Promise<{success: boolean, count: number, failed: number, message: string}>}
 */
export const requestDriverLocations = async () => {
    try {
        if (!functions) {
            throw new Error('Cloud Functions not initialized. Please set up Firebase Functions.');
        }

        const requestLocationsFn = httpsCallable(functions, 'requestDriverLocations');
        const result = await requestLocationsFn();

        console.log('requestDriverLocations result:', result.data);
        return result.data;
    } catch (error) {
        console.error('Error requesting driver locations:', error);

        // Provide user-friendly error messages
        if (error.code === 'functions/not-found') {
            throw new Error('Location request function not deployed. Please deploy Cloud Functions.');
        }
        if (error.code === 'functions/permission-denied') {
            throw new Error('You do not have permission to request driver locations.');
        }
        if (error.code === 'functions/unauthenticated') {
            throw new Error('You must be logged in to request driver locations.');
        }

        throw error;
    }
};

/**
 * Get all online drivers from Firestore
 * 
 * @param {Object} options - Query options
 * @param {boolean} options.onlyWithLocation - If true, only return drivers with valid location
 * @param {number} options.maxStaleMinutes - If set, filter out locations older than this many minutes
 * @returns {Promise<Array<{id, name, phone, latitude, longitude, locationTimestamp, vehicleType, rating}>>}
 */
export const getOnlineDrivers = async (options = {}) => {
    try {
        const { onlyWithLocation = true, maxStaleMinutes = null } = options;

        const usersRef = collection(db, 'users');
        const onlineDriversQuery = query(
            usersRef,
            where('type', '==', 'driver'),
            where('isOnline', '==', true)
        );

        const snapshot = await getDocs(onlineDriversQuery);
        const drivers = [];
        const now = Date.now();

        snapshot.forEach((doc) => {
            const data = doc.data();

            // Extract location data - handle multiple formats from your Firestore structure
            let latitude = null;
            let longitude = null;
            let locationTimestamp = null;

            // Primary: Direct latitude/longitude fields at root level
            if (data.latitude && data.longitude) {
                latitude = data.latitude;
                longitude = data.longitude;
            }

            // Fallback: driverLocation.geopoint (GeoPoint format)
            if (!latitude && data.driverLocation?.geopoint) {
                const geopoint = data.driverLocation.geopoint;
                latitude = geopoint.latitude ?? geopoint._lat;
                longitude = geopoint.longitude ?? geopoint._long;
            }

            // Handle timestamp - check multiple possible locations
            if (data.driverLocation?.lastUpdate) {
                locationTimestamp = data.driverLocation.lastUpdate.toDate
                    ? data.driverLocation.lastUpdate.toDate()
                    : new Date(data.driverLocation.lastUpdate);
            } else if (data.lastLocationUpdate) {
                locationTimestamp = data.lastLocationUpdate.toDate
                    ? data.lastLocationUpdate.toDate()
                    : new Date(data.lastLocationUpdate);
            }

            // Apply location filter
            if (onlyWithLocation && (!latitude || !longitude)) {
                return; // Skip drivers without location
            }

            // Apply staleness filter
            if (maxStaleMinutes && locationTimestamp) {
                const ageMinutes = (now - locationTimestamp.getTime()) / (1000 * 60);
                if (ageMinutes > maxStaleMinutes) {
                    return; // Skip stale locations
                }
            }

            drivers.push({
                id: doc.id,
                name: data.name || 'Unknown',
                phone: data.phoneNumber || data.phone || '',
                latitude,
                longitude,
                locationTimestamp,
                vehicleType: data.vehicleType || 'Unknown',
                rating: data.rating || 0,
                fcmToken: data.fcmToken || null,
                platform: data.platform || 'unknown'
            });
        });

        console.log(`getOnlineDrivers: Found ${drivers.length} drivers`);
        return drivers;
    } catch (error) {
        console.error('Error fetching online drivers:', error);
        throw error;
    }
};

/**
 * Convert a timestamp to a human-readable "time ago" string
 * 
 * @param {Date|number|{toDate: Function}} timestamp - The timestamp to convert
 * @returns {string} - "Just now", "30s ago", "2m ago", "1h ago", etc.
 */
export const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';

    let date;
    if (timestamp.toDate) {
        date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
        date = timestamp;
    } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
    } else {
        date = new Date(timestamp);
    }

    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

    if (seconds < 5) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};
